import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { HEADSHOT } from "../../lib/assets";
import { scrollToId } from "../../lib/scroll";

const EASE = [0.22, 1, 0.36, 1];
const lineUp = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.3 + i * 0.12, ease: EASE } }),
};

const HeadlineLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" variants={lineUp} custom={i} initial="hidden" animate="show">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section id="top" ref={ref} className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 overflow-hidden">
      {/* soft background accents */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-teal-100/60 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -left-32 h-[360px] w-[360px] rounded-full bg-sky-100/50 blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        {/* Left */}
        <div className="lg:col-span-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-teal-700 uppercase"
            data-testid="hero-overline"
          >
            <MapPin size={14} /> Pembina Trails School Trustee
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tighter leading-[1.02] text-slate-900 text-5xl md:text-6xl lg:text-7xl mt-6">
            <HeadlineLine i={0}>A Fresh <span className="text-teal-600">Voice</span></HeadlineLine>
            <HeadlineLine i={1}>For Our Schools</HeadlineLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-5 text-lg md:text-xl font-semibold text-slate-500 font-display"
          >
            Parent. Resident. Technology Leader.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.82 }}
            className="mt-6 text-base md:text-lg leading-relaxed text-slate-600 max-w-xl"
          >
            Pembina Trails continues to grow and evolve. Our schools deserve leadership
            that reflects the needs of today's students, families, and communities —
            fresh perspectives, practical experience, and a commitment to student success.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.95 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("#about")}
              className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white font-semibold px-7 py-4 hover:bg-teal-700 transition-colors"
              data-testid="hero-meet-button"
            >
              Meet Noman
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("#join")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 text-slate-900 font-semibold px-7 py-4 hover:bg-slate-900 hover:text-white transition-colors"
              data-testid="hero-join-button"
            >
              Join The Campaign
            </button>
          </motion.div>
        </div>

        {/* Right - masked image reveal */}
        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0% round 28px)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
            transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
            className="relative rounded-[28px] overflow-hidden bg-slate-100 border border-slate-200 shadow-2xl shadow-slate-900/10"
            data-testid="hero-image"
          >
            <motion.img
              src={HEADSHOT}
              alt="Noman Zaheer, candidate for School Trustee"
              style={{ y: imgY }}
              className="w-full h-[440px] sm:h-[560px] lg:h-[620px] object-cover object-top scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/25 via-transparent to-transparent" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
